import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateAgent } from "@/api/mutations/agents";
import { useUsers } from "@/api/queries/users";
import { useSettingsProfiles } from "@/api/queries/settingsProfiles";
import {
  validateCreateAgent,
  type CreateAgentInput,
  type ValidationError,
} from "@/api/schemas/agent";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wird mit Agent + Plain-Token gerufen sobald Master 201 returnt.
   *  Parent ist verantwortlich für Token-Display (sonst geht Token beim
   *  Dialog-Unmount verloren). */
  onCreated: (result: { agentId: string; token: string }) => void;
}

function resolveProfileIdFor(profileId: string | null | undefined, hasStandard: boolean): string | undefined {
  if (profileId === undefined) return hasStandard ? "standard" : undefined;
  if (profileId === null) return undefined;
  return profileId;
}

const EMPTY: CreateAgentInput = {
  id: "",
  name: "",
  description: "",
  type: "user-agent",
  ownerId: null,
};

export function CreateAgentDialog({ open, onOpenChange, onCreated }: Props) {
  const [form, setForm] = useState<CreateAgentInput>(EMPTY);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useCreateAgent();
  const usersQuery = useUsers();
  const profilesQuery = useSettingsProfiles();

  const profiles = profilesQuery.data ?? [];
  const hasStandard = profiles.some((p) => p.id === "standard");
  // #281: Vorschau, was das gewählte Profil als Blaupause mitbringt.
  const selectedProfile = profiles.find((p) => p.id === resolveProfileIdFor(form.profileId, hasStandard));
  const bp = selectedProfile?.blueprint;
  const bpParts = bp
    ? [
        bp.brainImageId ? "Brain-Image" : null,
        bp.plugins.length ? `${bp.plugins.length} Plugin${bp.plugins.length === 1 ? "" : "s"}` : null,
        bp.connectors.length ? `${bp.connectors.length} Connector${bp.connectors.length === 1 ? "" : "en"}` : null,
        bp.defaults.model ? `Modell ${bp.defaults.model}` : null,
        bp.defaults.monthlyBudgetUsd != null ? `Budget $${bp.defaults.monthlyBudgetUsd}/Monat` : null,
        bp.defaults.turnBudgetUsd != null ? `Turn-Cap $${bp.defaults.turnBudgetUsd}` : null,
      ].filter((x): x is string => x !== null)
    : [];
  const NONE = "__none__";

  // Dropdown-Wert: undefined (unberührt) → "standard" falls vorhanden, sonst "keins".
  const profileValue =
    form.profileId === undefined
      ? hasStandard
        ? "standard"
        : NONE
      : form.profileId === null
        ? NONE
        : form.profileId;

  // Effektiver Wert fürs Submit: undefined→standard (falls da), null→weglassen.
  function resolveProfileId(): string | undefined {
    return resolveProfileIdFor(form.profileId, hasStandard);
  }

  function fieldError(field: keyof CreateAgentInput) {
    return errors.find((e) => e.field === field)?.message;
  }

  function reset() {
    setForm(EMPTY);
    setErrors([]);
    setServerError(null);
  }

  function handleClose(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }

  function handleSubmit() {
    const v = validateCreateAgent(form);
    setErrors(v);
    setServerError(null);
    if (v.length > 0) return;

    createMutation.mutate({ ...form, profileId: resolveProfileId() }, {
      onSuccess: ({ agent, token, profileApplied }) => {
        toast.success(`Agent ${agent.id} erstellt`);
        // #281: nicht gesetzte Blaupausen-Einträge NICHT verschlucken — der Admin klickt nach.
        for (const w of profileApplied?.warnings ?? []) {
          toast.warning(`Blaupause unvollständig: ${w}`, { duration: 10_000 });
        }
        // Token an Parent geben BEVOR Close (sonst Token verloren beim Unmount)
        onCreated({ agentId: agent.id, token });
        onOpenChange(false);
      },
      onError: (err) => {
        const msg = (err as Error).message ?? "Unbekannter Fehler";
        setServerError(msg);
        toast.error(`Create fehlgeschlagen: ${msg}`);
      },
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="create-agent-dialog">
          <DialogHeader>
            <DialogTitle>Neuer Agent</DialogTitle>
            <DialogDescription>
              Erstellt einen Agent und gibt einen Token zurück (One-Time-Display).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="agent-id">ID *</Label>
              <Input
                id="agent-id"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                placeholder="alice"
                data-testid="field-id"
                autoFocus
              />
              {fieldError("id") && (
                <div className="text-xs text-red" data-testid="error-id">
                  {fieldError("id")}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="agent-name">Name *</Label>
              <Input
                id="agent-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Alice"
                data-testid="field-name"
              />
              {fieldError("name") && (
                <div className="text-xs text-red" data-testid="error-name">
                  {fieldError("name")}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="agent-description">Description *</Label>
              <Textarea
                id="agent-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                data-testid="field-description"
              />
              {fieldError("description") && (
                <div className="text-xs text-red" data-testid="error-description">
                  {fieldError("description")}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v as CreateAgentInput["type"] })}
                >
                  <SelectTrigger data-testid="field-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user-agent">user-agent</SelectItem>
                    <SelectItem value="service-agent">service-agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Owner (optional)</Label>
                <Select
                  value={form.ownerId ?? "__none__"}
                  onValueChange={(v) => setForm({ ...form, ownerId: v === "__none__" ? null : v })}
                >
                  <SelectTrigger data-testid="field-owner">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— ohne Owner</SelectItem>
                    {(usersQuery.data ?? []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Settings-Profil</Label>
              <Select
                value={profileValue}
                onValueChange={(v) => setForm({ ...form, profileId: v === NONE ? null : v })}
              >
                <SelectTrigger data-testid="field-profile">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— keins (leere Config)</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.id === "standard" ? " (Default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-subtext0">
                Config wird beim Erstellen als Snapshot übernommen. „Standard" macht den Agent
                out-of-the-box antwortfähig.
              </p>
              {bpParts.length > 0 && (
                <p className="text-xs text-text2" data-testid="create-agent-blueprint-preview">
                  Dieses Profil bringt mit: {bpParts.join(" · ")}
                </p>
              )}
            </div>
          </div>

          {serverError && (
            <div className="text-sm text-red" data-testid="create-server-error">
              {serverError}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleClose(false)} data-testid="create-cancel">
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              data-testid="create-submit"
            >
              {createMutation.isPending ? "..." : "Agent erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
